import type { AuthorStats, SvenManusPubliseringsvei } from '@/data/types';

export type SvenSjanger = {
  id: string;
  navn: string;
  beskrivelse: string;
  sterkeStats: ReadonlyArray<keyof AuthorStats>;
};

export const SVEN_SJANGRE: ReadonlyArray<SvenSjanger> = [
  {
    id: 'KRIM',
    navn: 'Krim',
    beskrivelse: 'Tett plot, sterk dialog, gåtefull stemning.',
    sterkeStats: ['plot', 'dialog'],
  },
  {
    id: 'LITERARY',
    navn: 'Litterær roman',
    beskrivelse: 'Språk og karakter står i sentrum.',
    sterkeStats: ['spraak', 'karakter', 'voice'],
  },
  {
    id: 'BARNEBOK',
    navn: 'Barnebok',
    beskrivelse: 'Stemme og autentisitet er alt.',
    sterkeStats: ['voice', 'autentisitet'],
  },
  {
    id: 'POETRY',
    navn: 'Lyrikk',
    beskrivelse: 'Hver linje må sitte. Disiplin og språk.',
    sterkeStats: ['spraak', 'disiplin'],
  },
  {
    id: 'NONFICTION',
    navn: 'Sakprosa',
    beskrivelse: 'Autentisitet og struktur over år.',
    sterkeStats: ['autentisitet', 'disiplin'],
  },
  {
    id: 'FANTASY',
    navn: 'Fantasy',
    beskrivelse: 'Verden bygget på plot og karakter.',
    sterkeStats: ['plot', 'karakter'],
  },
  {
    id: 'FEELGOOD',
    navn: 'Feelgood',
    beskrivelse: 'Varm stemme, hyggelige karakterer.',
    sterkeStats: ['voice', 'karakter'],
  },
  {
    id: 'ROMANCE',
    navn: 'Romance',
    beskrivelse: 'Dialog og følelser bærer.',
    sterkeStats: ['dialog', 'karakter'],
  },
];

export function finnSjanger(id: string): SvenSjanger | undefined {
  return SVEN_SJANGRE.find((s) => s.id === id);
}

const STAT_KEYS_ALL: ReadonlyArray<keyof AuthorStats> = [
  'dialog',
  'plot',
  'karakter',
  'spraak',
  'voice',
  'autentisitet',
  'disiplin',
];

export function snittStats(stats: AuthorStats): number {
  let sum = 0;
  for (const k of STAT_KEYS_ALL) sum += stats[k];
  return sum / STAT_KEYS_ALL.length;
}

export const DAGLIG_MIN = 5;
export const DAGLIG_MAX = 15;

export function beregnDaglig(stats: AuthorStats): number {
  const snitt = snittStats(stats);
  const raa = snitt * 0.15 + stats.disiplin * 0.05;
  if (raa < DAGLIG_MIN) return DAGLIG_MIN;
  if (raa > DAGLIG_MAX) return DAGLIG_MAX;
  return Math.round(raa * 10) / 10;
}

export function harSterkMatch(sjanger: SvenSjanger, stats: AuthorStats): boolean {
  for (const key of sjanger.sterkeStats) {
    if (stats[key] >= 70) return true;
  }
  return false;
}

export const TITLER: ReadonlyArray<string> = [
  'Mørke',
  'Vinternatt',
  'Det stille',
  'Tilbake til Sandefjord',
  'Hva vi ikke sa',
  'Sommeren før alt',
  'Stjerner og snø',
  'Det vi mistet',
  'Når isen smelter',
  'Brevet fra Bergen',
  'Underveis',
  'Halvveis hjem',
  'Trærne husker',
  'Etter regnet',
  'Lyset i Lofoten',
  'Mellom oss',
  'Hva mor sa',
  'Faren min',
  'Den lange veien',
  'Tilbake',
  'I går',
  'Det blå huset',
  'Når kvelden kommer',
  'Sårene gror',
  'Bak vinduet',
  'Stille vann',
  'Skyggene',
  'Hva som blir igjen',
  'Den lengste natten',
  'Før vi sov',
  'Når vi var unge',
  'Det jeg ikke fortalte',
  'Hjem til Halden',
  'Sangen om søsteren',
];

export function velgTittel(rng: () => number, brukteIHistorikk?: ReadonlyArray<string>): string {
  const brukte = new Set(brukteIHistorikk ?? []);
  const ledige = TITLER.filter((t) => !brukte.has(t));
  const liste = ledige.length > 0 ? ledige : TITLER;
  const idx = Math.floor(rng() * liste.length);
  return liste[Math.max(0, Math.min(liste.length - 1, idx))];
}

export const KAPELLE_TERSKEL_SNITT = 55;
export const ASKETANK_TERSKEL_SNITT = 70;
export const KAPELLE_BASIS_SJANSE = 0.6;
export const ASKETANK_BASIS_SJANSE = 0.3;

export const KAPELLE_FORSKUDD = 80_000;
export const ASKETANK_FORSKUDD = 200_000;
export const CALIDRIS_VANITY_KOSTNAD = 50_000;

export const EGET_FORLAG_OMDOMME = -10;
export const KAPELLE_AVSLAG_OMDOMME = -3;
export const ASKETANK_KONTRAKT_OMDOMME = 15;
export const ASKETANK_AVSLAG_OMDOMME = -2;
export const CALIDRIS_VANITY_OMDOMME = -20;
export const EGET_FORLAG_SALG_BOOST = 0.2;

export const SVEN_BOK_CALIDRIS_FLAG = 'sven_calidris_vanity';
export const SVEN_BOK_EGET_FORLAG_FLAG_PREFIKS = 'sven_egetforlag_';

export const SVEN_BIAS_EGET_FORLAG = -1;
export const SVEN_BIAS_CALIDRIS = -2;

export const SVEN_BRAGE_HANDICAP_EGET_FORLAG = 0.85;
export const SVEN_BRAGE_HANDICAP_CALIDRIS = 0.7;
export const SVEN_BRAGE_HANDICAP_NEUTRAL = 1.0;

export const SVEN_BRAGE_NOMINASJON_OMDOMME = 10;
export const SVEN_BRAGE_VINNER_OMDOMME = 50;
export const SVEN_BRAGE_VINNER_LOJALITET = 20;

export const SVEN_VANITY_SJANSE = 0.04;
export const SVEN_VANITY_KOSTNAD = 30_000;
export const SVEN_VANITY_AVSLAG_OMDOMME = 2;
export const SVEN_VANITY_AKSEPT_OMDOMME = -20;
export const SVEN_VANITY_AKSEPT_BIAS = -1;
export const SVEN_VANITY_LEKKE_ATTENTION = 15;
export const SVEN_VANITY_LEKKE_OMDOMME = 5;

export const SVEN_CALIDRIS_KICKBACK_SJANSE = 0.10;
export const SVEN_CALIDRIS_KICKBACK_VARIGHET_DAGER = 30;
export const SVEN_CALIDRIS_KICKBACK_PROSENT = 30;

export const SVEN_VANITY_FLAG = 'sven_vanity_godtok';

export function kapelleVurder(stats: AuthorStats, rng: () => number): boolean {
  const snitt = snittStats(stats);
  if (snitt < KAPELLE_TERSKEL_SNITT) return false;
  const bonus = Math.max(0, (snitt - KAPELLE_TERSKEL_SNITT) * 0.01);
  const sjanse = Math.min(0.9, KAPELLE_BASIS_SJANSE + bonus);
  return rng() < sjanse;
}

export function asketankVurder(stats: AuthorStats, rng: () => number): boolean {
  const snitt = snittStats(stats);
  if (snitt < ASKETANK_TERSKEL_SNITT) return false;
  const bonus = Math.max(0, (snitt - ASKETANK_TERSKEL_SNITT) * 0.015);
  const sjanse = Math.min(0.75, ASKETANK_BASIS_SJANSE + bonus);
  return rng() < sjanse;
}

export const GYLDIGE_PUBLISERINGSVEIER: ReadonlyArray<SvenManusPubliseringsvei> = [
  'eget-forlag',
  'kapelle',
  'asketank',
  'gullsand',
  'calidris',
];

export function genererSvenIsbn(dag: number, antallTidligere: number): string {
  const teller = (antallTidligere + 1) % 1000;
  const dagPart = (dag % 10000).toString().padStart(4, '0');
  const tellerPart = teller.toString().padStart(3, '0');
  return `9788299${dagPart}${tellerPart}`.slice(0, 13);
}
